<template>
    <x-container fluid app-width>
        <x-layout>
            <x-flex>
                <template v-if="loading">
                    <loading-spinner />
                </template>

                <x-container v-else fluid class="summary-container">
                    <x-layout v-if="isPreFirmOrder" class="summary-header-row">
                        <x-flex xs12 text-xs-right>
                            <x-btn
                                color="tertiary"
                                small
                                @click="goBack"
                            >
                                <x-icon class="mr-2">arrow_back</x-icon>

                                Back
                            </x-btn>

                            <x-btn
                                class="ml-2"
                                color="tertiary"
                                small
                                @click="previewMRCDoc"
                            >
                                <i class="material-icons mr-2">browser_updated</i>

                                Download draft MRC
                            </x-btn>

                            <x-btn
                                class="ml-4"
                                color="primary"
                                small
                                :disabled="submitting"
                                @click="submitFirmOrder"
                            >
                                Submit

                                <x-icon class="ml-2">trending_flat</x-icon>
                            </x-btn>
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs12>
                            <summary-tabs-buttons
                                v-model="activeTab"
                                :tabs="summaryTabs"
                            />
                        </x-flex>
                    </x-layout>

                    <template v-if="activeTab === 'product'">
                        <x-layout>
                            <x-flex xs6 class="left">
                                <x-card class="panel">
                                    <x-card-title>
                                        <h3>Submission information</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <standalone-policy-summary-data-list
                                            v-if="isStandalonePolicy"
                                            v-model="submission"
                                            :multipleEnitities="standalonePolicyWithMultipleEntities"
                                        />

                                        <summary-data-list v-else v-model="submission" />
                                    </x-card-text>
                                </x-card>
                            </x-flex>

                            <x-flex xs6 class="right">
                                <x-card v-if="!isPreFirmOrder" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Firm order</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <broker-firm-order-table
                                            :firmOrder="firmOrder"
                                            :submissionCurrency="submission.currency.code"
                                        />
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="standalonePolicyWithMultipleEntities" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Entities</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <submission-entity-data-list v-model="submission.entities"/>
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="hasObligor" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Obligors</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <obligor-data-list v-model="submission.obligors" obligor :currency-code="submission.currency.code" />
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="hasObligor && submission.guarantors.length > 0" class="panel">
                                    <x-card-title>
                                        <h3>Guarantors</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <obligor-data-list v-model="submission.guarantors" />
                                    </x-card-text>
                                </x-card>
                            </x-flex>
                        </x-layout>
                    </template>

                    <template v-if="activeTab === 'additional_information'">
                        <x-layout>
                            <x-flex>
                                <additional-information-tab
                                    v-model="submission.information_sections"
                                    :insurer-list="insurerList"
                                />
                            </x-flex>
                        </x-layout>
                    </template>

                    <template v-if="activeTab === 'market_response_sheet'">
                        <x-layout>
                            <x-flex>
                                <market-response-sheet-tab :risk="submission" />
                            </x-flex>
                        </x-layout>
                    </template>

                    <template v-if="activeTab === 'documents'">
                        <document-list
                            :insurer-list="insurerList"
                            insurer-specific-upload
                            :facility-id="submission.facility_id"
                            upload
                            :documentable-id="submission.id"
                            documentable-type="submission" :is-standalone-policy="submission.risk_structure === 'standalone_policy'"
                            :isPreFirmOrder="isPreFirmOrder"
                            :showMRCOnly="isPreFirmOrder"
                            @update-documents="updateDocuments"
                            @documents-updated="proceedToGoBack"
                        />
                    </template>
                </x-container>
            </x-flex>
        </x-layout>

        <modal
            ref="closeSubmission"
            title="Close submission"
            confirmation
        >
            <p>
                Are you sure you want to close this Enquiry for
                {{submission.client_company?.name}}/{{submission.obligor_name}}, this will also close all
                underwriter
                offers and cancel the current firm order?
            </p>
        </modal>

        <modal ref="deleteModal" confirmation title="Delete document" action-text="Delete">
            <p>{{ mrcDocument.filename }}</p>
            <p>Are you sure you want to delete this file?</p>
        </modal>
    </x-container>
</template>

<script>
    import SummaryTabsButtons from '~/components/Shared/UIComponents/SummaryTabsButtons';
    import DocumentList from '~/components/Shared/UIComponents/DocumentList.vue';
    import SummaryDataList from '~/components/Shared/UIComponents/SummaryDataList';
    import AdditionalInformationTab from '~/components/Shared/UIComponents/AdditionalInformationTab';
    import BrokerFirmOrderTable from '~/components/Broker/UIComponents/BrokerFirmOrderTable';
    import Modal from '~/components/Shared/UIComponents/Modal';
    import ObligorDataList from "~/components/Shared/UIComponents/ObligorDataList";
    import StandalonePolicySummaryDataList from '../../../components/Shared/UIComponents/StandalonePolicySummaryDataList.vue';
    import SubmissionEntityDataList from '../../../components/Shared/UIComponents/SubmissionEntityDataList.vue';
    import MarketResponseSheetTab from "../../../components/Broker/UIComponents/MarketResponseSheetTab.vue";

    export default {
        name: 'BrokerFirmOrderSummary',
        components: {
            SummaryTabsButtons,
            MarketResponseSheetTab,
            SummaryDataList,
            AdditionalInformationTab,
            DocumentList,
            BrokerFirmOrderTable,
            Modal,
            ObligorDataList,
            StandalonePolicySummaryDataList,
            SubmissionEntityDataList,
        },
        props: {
            firmOrder: Object,
            currentStep: Number,
            isManualFirmOrder: {
                type: Boolean,
                default: false
            },
            selectedInsurersUsersList: {
                type: Object,
                default() {
                    return {}
                }
            },
        },
        data() {
            return {
                loading: true,
                activeTab: 'product',
                submission: {},
                openSubmissionInformationPanelIndex: 0,
                openFirmOrderPanelIndex: 0,
                closing: false,
                documents: [],
                mrcDocument: {},
                submitting: false,
            }
        },
        created() {
            this.fetchData();

            this.$eventBus.$on('action-close-risk', () => this.closeSubmission());
        },
        methods: {
            async fetchData() {
                this.submission = (await axios.get(this.$root.api(`broker/submission/${this.$route.params.id}`))).data.data;
                if (!this.firmOrder) {
                    this.firmOrder = (await axios.get(this.$root.api(`broker/firm-order/${this.$route.params.id}`))).data.data;
                }
                this.loading = false;
            },
            async closeSubmission() {
                if (await this.$refs.closeSubmission.open() === false) {
                    return;
                }

                this.closing = true;

                await axios.put(this.$root.api(`broker/submission/${this.$route.params.id}/close`)).then(() => {
                    this.goToDashboard();
                }).then(() => {
                    this.closing = false;
                });
            },
            goToDashboard() {
                this.$router.push({name: 'broker-dashboard'});
            },
            async previewMRCDoc() {
                let url = await axios.get(`/api/broker/submission/${this.submission.id}/accepted-declaration/draft-mrc-download`);

                window.location.href = url.data.url;
            },
            goToStep(step) {
                this.$emit('firm-order:navigate-to', step);
            },
            updateDocuments(documents) {
                this.documents = documents;
            },
            async submitFirmOrder() {
                this.submitting = true;
                if (this.isManualFirmOrder) {
                    await axios.post(`/api/broker/submission/${this.submission.id}/manual-firm-order`, this.firmOrder);
                } else {
                    await axios.post(`/api/broker/submission/${this.submission.id}/firm-order`, this.firmOrder);
                }
                this.submitting = false;
                this.$router.push({path: '/agent/dashboard'});
            },
            async goBack() {
                this.mrcDocument = this.documents.find(doc => doc.document_category_key === 'market_reform_contract');
                if (!this.mrcDocument) {
                    this.proceedToGoBack();
                    return false;
                }

                if (await this.$refs.deleteModal.open() === true) {
                    await axios.delete(`/api/documents/${this.mrcDocument.id}`);
                    this.proceedToGoBack();
                }
            },
            proceedToGoBack() {
                this.goToStep(this.currentStep - 1);
            },
        },
        computed: {
            summaryTabs() {
                const tabs = [
                    {
                        value: 'product',
                        text: 'Product',
                    },
                ];

                if (this.submission.information_sections.length > 0) {
                    tabs.push({
                        value: 'additional_information',
                        text: 'Additional Information',
                    })
                }

                if (!this.isManualFirmOrder) {
                    tabs.push(
                        {
                            value: 'market_response_sheet',
                            text: 'Market Response Sheet',
                        }
                    );
                }

                tabs.push(
                    {
                        value: 'documents',
                        text: 'Documents',
                    },
                );

                return tabs;
            },
            isStandalonePolicy() {
                return this.submission.risk_structure === 'standalone_policy';
            },
            standalonePolicyWithMultipleEntities() {
                return this.isStandalonePolicy && this.submission.entities.length > 1;
            },
            actionMenuItems() {
                return [
                    {
                        name: 'Close risk',
                        eventName: 'action-close-risk'
                    }
                ];
            },
            hasObligor() {
                return this.submission.hasOwnProperty('product') && !this.submission.product.has_no_obligor;
            },
            firmOrderOffers: function() {
                return this.firmOrder.firm_order_offers?.filter(offer => ['firm_order', 'accepted', 'cancelled', 'captive_reviewing'].includes(offer.status));
            },
            offers() {
                return this.firmOrder.offers;
            },
            insurerList() {
                const insurerList = this.isPreFirmOrder ? this.offers.map(offer => offer.insurer) : this.firmOrderOffers.map(offer => offer.insurer_company);
                return insurerList.filter(insurer => Boolean(insurer));
            },
            isPreFirmOrder() {
                return ['in_the_market', 'draft'].includes(this.submission.status);
            },
        },
        watch: {
            actionMenuItems: {
                handler(newItems) {
                    this.$eventBus.$emit('update-action-menu-items', newItems);
                },
                immediate: true
            }
        }
    }
</script>

<style scoped lang="scss">
.summary-header-row {
    margin-bottom: 32px;
}
</style>
